import {
    generateProductMap,
    getAssetTypes,
    getProductsForWSByWSId,
} from "api/ProductMaster";
import { getWorkspace } from "api/Workspace";
import { useContext, useEffect, useState } from "react";
import { sortListByAssetTypeStandardOrder } from "utils/PortfolioUtils";

import { PageEditorContext } from "components/PageEditor";
import { PortfolioContext } from "components/Portfolio";

export const useProductListData = (workspaceId, portfolios, amData) => {
  const [productList, setProductList] = useState(null);
  const [ports, setPorts] = useState(null);
  const [ready, setReady] = useState(false);

  const { assetNames: portAssetNames } = useContext(PortfolioContext);
  const { assetNames: pageAssetNames } = useContext(PageEditorContext);
  const assetNames = portAssetNames || pageAssetNames;

  useEffect(() => {
    if (!workspaceId) return;

    getWorkspace(workspaceId)
      .then((res) => {
        return res;
      })
      .then((ws) => {
        return getProductsForWSByWSId(workspaceId).then((res) => {
          const productMap = generateProductMap(res);
          return { ws, productMap };
        });
      })
      .then(({ ws, productMap }) => {
        return getAssetTypes().then((assetTypes) => {
          return { ws, productMap, assetTypes };
        });
      })
      .then(({ ws, productMap, assetTypes }) => {
        const selectedProducts = ws?.portfolio_set?.selected_products || [];
        const selectedProductsWithName = selectedProducts
          .filter((p) => {
            const product = productMap[p.product_id];
            return product && product.product_name;
          })
          .map((p) => {
            const productsIndex =
              amData.get(p.asset_type)?.get("productsIndex") || {};
            const product = productMap[p.product_id];
            return {
              asset: p.asset_type,
              id: p.product_id,
              name: product.product_name,
              risk: product.risk,
              return: product.return,
              index: productsIndex[product.product_name] || 100000,
            };
          });
        return { ws, selectedProducts: selectedProductsWithName, assetTypes };
      })
      .then(({ ws, assetTypes, selectedProducts }) => {
        const assetTypeIndex = {};
        assetTypes.forEach((asset, i) => {
          assetTypeIndex[asset] = i;
        });
        const ports = {};
        if (
          ws?.portfolio_set?.current_portfolio &&
          ws?.portfolio_set?.current_portfolio.selected_for_report
        ) {
          const portMap = {};
          const total = ws.portfolio_set.current_portfolio.investment_budget;
          ws?.portfolio_set?.current_portfolio?.compositions?.forEach((c) => {
            if (!(c.asset_type in portMap)) {
              portMap[c.asset_type] = {};
            }
            portMap[c.asset_type][c.product_id] = {
              ...c,
              assignRate: c.amount / total,
            };
          });
          ports[ws.portfolio_set.current_portfolio.name] = portMap;
        }
        if (ws?.portfolio_set?.proposed_portfolios) {
          ws?.portfolio_set?.proposed_portfolios
            ?.filter(({ selected_for_report }) => selected_for_report)
            ?.forEach((port) => {
              const portMap = {};
              const total = port.investment_budget;
              port?.compositions?.forEach((c) => {
                if (!(c.asset_type in portMap)) {
                  portMap[c.asset_type] = {};
                }
                portMap[c.asset_type][c.product_id] = {
                  ...c,
                  assignRate: c.amount / total,
                };
              });
              ports[port.name] = portMap;
            });
        }

        return sortListByAssetTypeStandardOrder(
          selectedProducts,
          "asset",
          "index",
          (a, b) => a - b
        ).then((sortedSelectedProducts) => {
          return {
            selectedProducts: sortedSelectedProducts,
            ports,
            assetTypeIndex,
          };
        });
      })
      .then(({ selectedProducts, ports, assetTypeIndex }) => {
        const result = selectedProducts.map((p) => {
          let assigned = false;
          Object.entries(ports).forEach(([portName, portMap]) => {
            if (
              portMap.hasOwnProperty(p.asset) &&
              portMap[p.asset].hasOwnProperty(p.id) &&
              portMap[p.asset][p.id].assignRate
            ) {
              p[portName] = portMap[p.asset][p.id];
              assigned = true;
            } else {
              p[portName] = { assignRate: "-" };
            }
          });
          p.assigned = assigned;
          return p;
        });
        const filteredResult = result.filter((p) => p.assigned);
        const sortedResult = filteredResult.sort(
          (a, b) => assetTypeIndex[a.asset] - assetTypeIndex[b.asset]
        );
        setProductList(sortedResult);
        setPorts(Object.keys(ports));
      });
  }, [workspaceId, portfolios]);

  useEffect(() => {
    if (productList && ports) setReady(true);
  }, [productList, ports]);

  const sortedPortNames = ports ? [
    "現状",
    ...ports
      .filter((port) => port !== "現状")
      .sort((a, b) => a.localeCompare(b)),
  ] : [];

  const getAssetName = (assetType) => {
    return assetType in assetNames ? assetNames[assetType] : assetType;
  };

  return {
    productList,
    ports,
    sortedPortNames,
    ready,
    getAssetName,
  };
};
